<script>
import BaseClose from './BaseClose.vue'
import { mapState, mapMutations } from 'vuex'

const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

export default {
  name: 'BaseOverlay',

  components: { BaseClose },

  props: {
    name: {
      type: String,
      required: true
    }
  },

  emits: ['close'],

  data () {
    return {
      firstFocusableElement: null,
      focusableContent: null,
      lastFocusableElement: null
    }
  },

  computed: {
    ...mapState({ currentOverlay: 'currentOverlay' }),

    isOpen () {
      return this.currentOverlay === this.name
    }
  },

  watch: {
    isOpen (is) {
      if (is) {
        this.trapFocus()
      }
    }
  },
  
  methods: {
    ...mapMutations(['SET_CURRENT_OVERLAY']),

    close () {
      this.SET_CURRENT_OVERLAY({ name: 'none' })
      this.$emit('close')
    },

    trapFocus () {
      setTimeout(() => {
        this.firstFocusableElement = this.$el.querySelectorAll(focusableElements)[0]
        this.focusableContent = this.$el.querySelectorAll(focusableElements)
        this.lastFocusableElement = this.focusableContent[this.focusableContent.length - 1]

        this.firstFocusableElement.focus()

        this.$el.addEventListener('keydown', this.handleKeydown)
      }, 600) // give the overlay time to open first, because focusing manipulates scroll
    },

    handleKeyup (e) {
      if (e.key === 'Escape' || e.code === 'Escape') {
        this.close()
      }
    },

    handleKeydown (e) {
      let isTabPressed = e.key === 'Tab' || e.keyCode === 9

      if (!isTabPressed) {
        return
      }

      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusableElement) {
          this.lastFocusableElement.focus()
          e.preventDefault()
        }
      } else {
        if (document.activeElement === this.lastFocusableElement) {
          this.firstFocusableElement.focus()
          e.preventDefault()
        }
      }
    },
  }
}
</script>

<template>
  <div
    :class="[
      'overlay',
      `overlay--${name}`,
      { 'open': isOpen }
    ]"
    tabindex="-1"
    role="dialog"
    :aria-hidden="!isOpen"
    :aria-label="name"
    aria-modal="true"
    @keyup="handleKeyup"
  >
    <div
      class="overlay__scrim"
      aria-hidden="true"
      @click="close"
    />
    
    <div class="overlay__content">
      <div class="overlay__scroll-container">
        <slot />
      </div>

      <BaseClose
        :is-overlay="true"
        @click="close"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '../scss/utils/variables';
@import '../scss/utils/colors';
@import '../scss/utils/mixins';

.overlay {
	position: fixed;
	z-index: 999;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  visibility: hidden;
  opacity: 0;
  transition: visibility 0s linear $transition-duration,
    opacity $transition-duration $ease;
  &.open {
    visibility: visible;
    opacity: 1;
    transition: visibility 0s linear 0s,
      opacity $transition-duration $ease;
  }
	&__scrim {
    display: block;
		background-color: $color-scrim-dark;
		position: fixed;
		width: 100vw;
		height: 100vh;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		// visibility: hidden;
	}
	&__content {
		position: fixed;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
    transform: translate(-$width-gutter, $width-gutter);
		transition: transform $transition-duration $ease;
		.overlay.open & {
      transform: translate(0px, 0px);
			transition: transform $transition-duration $ease;
		}
		// .overlay--cart & {
    //   width: auto;
    //   height: auto;
    //   left: auto;
    //   right: 0;
		// }
	}
	&__scroll-container {
		position: relative;
		max-height: 100vh;
		overflow-y: scroll;
		-webkit-overflow-scrolling: touch;
		@include hide-scrollbar();
    padding-top: var(--height-header);
	}
}

// @media (prefers-reduced-motion: reduce) {
// 	.overlay {
// 		&__content {
// 			transition: none;
// 			&--search {
// 				transition: none;
// 				.body--scrolled & {
// 					transition: none;
// 				}
// 			}
// 		}
// 	}
// }

// @media screen and (max-width: $max-width-mobile) {
// 	.overlay {
// 		&__content {
// 			padding: $height-header-mobile $width-gutter-mobile $margin-bottom-page-content;
// 			&--right {
// 				width: 100%;
// 				min-width: 100%;
// 			}
// 		}
// 		&__close {
// 			top: $width-gutter-mobile;
// 			right: $width-gutter-mobile + $width-gutter-grid;
// 			width: $height-button-mobile;
// 			height: $height-button-mobile;
// 			padding: 0;
// 		}
// 		&__scroll-container {
// 			max-height: calc(100vh - #{$height-header-mobile});
// 			padding-bottom: $height-header-mobile;
// 		}
// 		&__search-form-wrapper {
// 			display: block;
// 		}
// 	}
// }
</style>